import React, { useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Calendar, Clock, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import ReviewCarousel from '../components/ReviewCarousel';
import SEO from '../components/SEO';
import JsonLd from '../components/JsonLd';
import ShortAnswer from '../components/ShortAnswer';
import { fetchBlogPost, fetchBlogPosts, formatPostDate, initialBlogData, postDates } from '../lib/blogApi';
import { blogPostingSchema, postAuthor } from '../lib/schema';
import { AUTHOR, authorRole } from '../lib/site';

// The post built into the page, if it is the one being shown.
const builtInPost = (slug) => {
  const data = initialBlogData();
  return data?.post && data.post.slug === slug ? data.post : undefined;
};

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(() => builtInPost(slug)); // undefined = loading, null = not found
  const [allPosts, setAllPosts] = useState(() => initialBlogData()?.list || []);

  useEffect(() => {
    setPost(builtInPost(slug));
    fetchBlogPost(slug).then(setPost).catch(() => setPost(builtInPost(slug) ?? null));
    fetchBlogPosts().then(setAllPosts).catch(() => setAllPosts([]));
    window.scrollTo(0, 0);
  }, [slug]);

  if (post === undefined) {
    return (
      <div className="min-h-screen bg-white pt-28 container mx-auto px-4 max-w-3xl space-y-6" data-testid="blog-post-loading">
        <Skeleton className="h-64 w-full rounded-2xl" />
        <Skeleton className="h-10 w-3/4" />
        <Skeleton className="h-6 w-full" />
        <Skeleton className="h-6 w-5/6" />
      </div>
    );
  }

  if (post === null) {
    return <Navigate to="/blog" replace />;
  }

  const relatedPosts = allPosts.filter(p => p.slug !== slug).slice(0, 3);
  const dates = postDates(post);
  const otherAuthor = postAuthor(post);

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title={post.seo?.meta_title || post.title}
        description={post.seo?.meta_description || post.excerpt}
        canonical={post.seo?.canonical || `/blog/${slug}`}
        image={post.cover_image_url}
        type="article"
      />
      <JsonLd data={blogPostingSchema(post)} />
      {/* Hero Image */}
      <div className="relative h-[40vh] md:h-[50vh]">
        <img 
          src={post.cover_image_url} 
          alt={`${post.title} – painting and decorating in Kent`}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        
        {/* Back Button */}
        <Link 
          to="/blog"
          className="absolute top-24 left-4 md:left-8 bg-white/90 hover:bg-white text-slate-900 rounded-full px-4 py-2 flex items-center gap-2 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Blog
        </Link>
      </div>

      {/* Article Content */}
      <article className="relative -mt-20 md:-mt-32" data-testid="blog-post-article">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12">
            {/* Category */}
            <span className="inline-block bg-cyan-100 text-cyan-700 px-4 py-1 rounded-full text-sm font-semibold mb-6">
              {post.category}
            </span>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-6 leading-tight" data-testid="blog-post-title">
              {post.title}
            </h1>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 md:gap-6 text-sm text-slate-500 mb-8 pb-8 border-b border-slate-200">
              <span className="flex items-center gap-2" data-testid="blog-post-author">
                <User className="h-4 w-4" />
                {otherAuthor ? `Written by ${otherAuthor}` : (
                  <span>
                    Written by <Link to={AUTHOR.url} className="hover:text-cyan-600 underline-offset-2 hover:underline">{AUTHOR.name}</Link>, {authorRole()}
                  </span>
                )}
              </span>{' '}
              <span className="flex items-center gap-2" data-testid="blog-post-published">
                <Calendar className="h-4 w-4" />
                <span>Published <time dateTime={dates.published}>{formatPostDate(dates.published)}</time></span>
              </span>
              {dates.modified && (
                <span className="flex items-center gap-2" data-testid="blog-post-updated">
                  <Calendar className="h-4 w-4" />
                  <span>Last updated <time dateTime={dates.modified}>{formatPostDate(dates.modified)}</time></span>
                </span>
              )}
              <span className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                {post.read_time}
              </span>
            </div>

            <ShortAnswer slug={post.slug} />

            {/* Content */}
            <div 
              className="blog-content max-w-none"
              dangerouslySetInnerHTML={{ __html: post.content_html }}
              data-testid="blog-post-content"
              style={{
                '--tw-prose-body': '#475569',
                '--tw-prose-headings': '#0e7490',
              }}
            />
            <style>{`
              .blog-content p {
                color: #475569;
                font-size: 1.125rem;
                line-height: 1.8;
                margin-bottom: 1.5rem;
              }
              .blog-content h2 {
                color: #0e7490;
                font-size: 1.5rem;
                font-weight: 700;
                margin-top: 2.5rem;
                margin-bottom: 1rem;
                padding-bottom: 0.5rem;
                border-bottom: 2px solid #e2e8f0;
              }
              .blog-content h3 {
                color: #0e7490;
                font-size: 1.25rem;
                font-weight: 600;
                margin-top: 2rem;
                margin-bottom: 0.75rem;
              }
              .blog-content ul {
                margin: 1.5rem 0;
                padding-left: 1.5rem;
                list-style-type: disc;
              }
              .blog-content ol {
                margin: 1.5rem 0;
                padding-left: 1.5rem;
                list-style-type: decimal;
              }
              .blog-content blockquote {
                margin: 1.5rem 0;
                padding: 0.5rem 1.5rem;
                border-left: 4px solid #06b6d4;
                color: #334155;
                font-style: italic;
                background: #f8fafc;
              }
              .blog-content img {
                max-width: 100%;
                height: auto;
                border-radius: 0.75rem;
                margin: 1.5rem 0;
              }
              .blog-content hr {
                margin: 2.5rem 0;
                border-color: #e2e8f0;
              }
              .blog-content table {
                width: 100%;
                border-collapse: collapse;
                margin: 1.5rem 0;
                font-size: 1rem;
              }
              .blog-content th, .blog-content td {
                border: 1px solid #e2e8f0;
                padding: 0.75rem;
                text-align: left;
                color: #475569;
              }
              .blog-content th {
                background: #f0fdfa;
                color: #0e7490;
                font-weight: 600;
              }
              .blog-content code {
                background: #f1f5f9;
                padding: 0.125rem 0.375rem;
                border-radius: 0.25rem;
                font-size: 0.95em;
              }
              .blog-content li {
                color: #475569;
                font-size: 1.125rem;
                line-height: 1.8;
                margin-bottom: 0.5rem;
                padding-left: 0.5rem;
              }
              .blog-content a {
                color: #0891b2;
                text-decoration: underline;
                text-underline-offset: 2px;
              }
              .blog-content a:hover {
                color: #0e7490;
              }
              .blog-content .lead {
                font-size: 1.25rem;
                color: #334155;
                line-height: 1.8;
                margin-bottom: 2rem;
                padding: 1rem 1.5rem;
                border-left: 4px solid #06b6d4;
                background: #f0fdfa;
              }
              .blog-content strong {
                color: #1e293b;
                font-weight: 600;
              }
            `}</style>

            {/* CTA Box */}
            <div className="mt-12 p-8 bg-gradient-to-br from-cyan-50 to-teal-50 rounded-2xl">
              <h3 className="text-xl font-bold text-slate-900 mb-4">
                Planning a decorating project in Kent?
              </h3>
              <p className="text-slate-600 mb-6">
                Contact Paint It Better for a free, no-obligation quote. We're a Checkatrade-approved painter and decorator serving Ashford, Canterbury, Folkestone, and all of Kent.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-full px-6">
                    Get Free Quote
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <a 
                  href="https://www.checkatrade.com/trades/PaintItBetterLtd"
                  target="_blank"
                  rel="noopener"
                >
                  <Button variant="outline" className="border-cyan-600 text-cyan-600 hover:bg-cyan-50 rounded-full px-6">
                    View Checkatrade Reviews
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-slate-900 mb-8">More Articles</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {relatedPosts.map((relatedPost) => (
                <Link key={relatedPost.id} to={`/blog/${relatedPost.slug}`}>
                  <div className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow group">
                    <div className="h-40 overflow-hidden">
                      <img 
                        src={relatedPost.cover_image_url} 
                        alt={relatedPost.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-5">
                      <h3 className="font-bold text-slate-900 group-hover:text-cyan-600 transition-colors line-clamp-2">
                        {relatedPost.title}
                      </h3>
                      <p className="text-sm text-slate-500 mt-2">{relatedPost.read_time}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Review Carousel */}
      <ReviewCarousel />
    </div>
  );
};

export default BlogPost;
